import type { VoiceGatewaySessionDescriptor, TurnServerDescriptor } from "@/types/assistant";

type VoiceTransportEvents = {
  onConnectionStateChange?: (state: RTCPeerConnectionState) => void;
  onError?: (error: Error) => void;
  onLevelUpdate?: (level: number) => void;
};

export class VoiceTransport {
  private readonly events: VoiceTransportEvents;
  private peerConnection: RTCPeerConnection | null = null;
  private dataChannel: RTCDataChannel | null = null;
  private localStream: MediaStream | null = null;
  private remoteAudio: HTMLAudioElement | null = null;
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private levelRaf: number | null = null;

  constructor(events?: VoiceTransportEvents) {
    this.events = events ?? {};
  }

  async connect(descriptor: VoiceGatewaySessionDescriptor): Promise<void> {
    if (typeof window === "undefined") {
      throw new Error("Voice transport is only available in the browser.");
    }

    await this.disposeInternal();

    try {
      this.localStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          noiseSuppression: true,
          echoCancellation: true,
          channelCount: 1,
        },
      });
    } catch (error) {
      throw new Error((error as Error)?.message ?? "Microphone permission denied");
    }

    const configuration: RTCConfiguration = {
      iceServers: this.buildIceServers(descriptor.turnServers),
      bundlePolicy: "max-bundle",
    };

    const pc = new RTCPeerConnection(configuration);
    this.peerConnection = pc;

    pc.onconnectionstatechange = () => {
      const state = pc.connectionState;
      if (state === "failed" || state === "closed" || state === "disconnected") {
        this.events.onError?.(new Error(`Voice connection ${state}`));
      }
      this.events.onConnectionStateChange?.(state);
    };

    pc.oniceconnectionstatechange = () => {
      if (pc.iceConnectionState === "failed") {
        this.events.onError?.(new Error("Voice ICE negotiation failed"));
      }
    };

    pc.ontrack = (event) => {
      const [stream] = event.streams;
      if (stream) {
        this.attachRemoteStream(stream);
      }
    };

    this.dataChannel = pc.createDataChannel("oai-events");
    this.dataChannel.onmessage = (event) => {
      if (typeof event.data === "string") {
        console.debug("Voice data", event.data);
      }
    };

    if (this.localStream) {
      this.localStream.getAudioTracks().forEach((track) => {
        pc.addTrack(track, this.localStream!);
      });
      this.beginLevelMonitoring(this.localStream);
    }

    const offer = await pc.createOffer({ offerToReceiveAudio: true, offerToReceiveVideo: false });
    await pc.setLocalDescription(offer);
    await this.waitForIceGathering(pc);

    const localSdp = pc.localDescription?.sdp ?? "";
    const response = await fetch(descriptor.provider.url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${descriptor.provider.apiKey}`,
        "Content-Type": "application/sdp",
        "OpenAI-Beta": "realtime=v1",
      },
      body: localSdp,
    });

    if (!response.ok) {
      throw new Error(`Voice provider rejected offer (${response.status})`);
    }

    const answer = await response.text();
    await pc.setRemoteDescription({ type: "answer", sdp: answer });
  }

  setMuted(muted: boolean) {
    if (!this.localStream) {
      return;
    }
    this.localStream.getAudioTracks().forEach((track) => {
      track.enabled = !muted;
    });
  }

  async disconnect(): Promise<void> {
    await this.disposeInternal();
  }

  private attachRemoteStream(stream: MediaStream) {
    if (!this.remoteAudio) {
      this.remoteAudio = new Audio();
      this.remoteAudio.autoplay = true;
      // Safari/iOS expect the playsinline attribute for inline audio streams,
      // but HTMLAudioElement does not expose the property in our TypeScript
      // lib definitions. Setting the attribute keeps playback inline without
      // upsetting the compiler.
      this.remoteAudio.setAttribute("playsinline", "true");
    }
    this.remoteAudio.srcObject = stream;
    const playPromise = this.remoteAudio.play();
    if (playPromise) {
      playPromise.catch(() => undefined);
    }
  }

  private beginLevelMonitoring(stream: MediaStream) {
    try {
      const audioContext = new AudioContext();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      const sampleData = new Uint8Array(analyser.frequencyBinCount);

      const updateLevel = () => {
        analyser.getByteTimeDomainData(sampleData);
        let sum = 0;
        for (let index = 0; index < sampleData.length; index += 1) {
          const value = (sampleData[index] - 128) / 128;
          sum += value * value;
        }
        const rms = Math.sqrt(sum / sampleData.length);
        const normalized = Math.min(1, rms * 2);
        this.events.onLevelUpdate?.(normalized);
        this.levelRaf = window.requestAnimationFrame(updateLevel);
      };

      updateLevel();
      this.audioContext = audioContext;
      this.analyser = analyser;
    } catch {
      this.audioContext = null;
      this.analyser = null;
      this.levelRaf = null;
    }
  }

  private stopLevelMonitoring() {
    if (this.levelRaf) {
      window.cancelAnimationFrame(this.levelRaf);
      this.levelRaf = null;
    }
    if (this.audioContext) {
      this.audioContext.close().catch(() => undefined);
      this.audioContext = null;
    }
    this.analyser = null;
  }

  private buildIceServers(turnServers: TurnServerDescriptor[] | undefined): RTCIceServer[] {
    if (!turnServers || turnServers.length === 0) {
      return [{ urls: ["stun:stun.l.google.com:19302"] }];
    }
    return turnServers.map((server) => {
      const urls = server.urls?.length ? server.urls : ["stun:stun.l.google.com:19302"];
      const entry: RTCIceServer = { urls };
      if (server.username) {
        entry.username = server.username;
      }
      if (server.credential) {
        entry.credential = server.credential;
      }
      return entry;
    });
  }

  private waitForIceGathering(connection: RTCPeerConnection): Promise<void> {
    if (connection.iceGatheringState === "complete") {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      const handleStateChange = () => {
        if (connection.iceGatheringState === "complete") {
          connection.removeEventListener("icegatheringstatechange", handleStateChange);
          resolve();
        }
      };
      connection.addEventListener("icegatheringstatechange", handleStateChange);
    });
  }

  private async disposeInternal() {
    this.stopLevelMonitoring();
    this.dataChannel?.close();
    this.dataChannel = null;

    if (this.peerConnection) {
      this.peerConnection.onconnectionstatechange = null;
      this.peerConnection.oniceconnectionstatechange = null;
      this.peerConnection.ontrack = null;
      this.peerConnection.close();
      this.peerConnection = null;
    }

    if (this.localStream) {
      this.localStream.getTracks().forEach((track) => track.stop());
      this.localStream = null;
    }

    if (this.remoteAudio) {
      this.remoteAudio.srcObject = null;
    }
  }
}
